(function (window) {
  /**
   * The BatchGeoStoreLocator is a multi-component module that generates a
   * vertical list of all the location in the locationData setting you pass.
   * It also uses Clusterize so it can handle large datasets. Clicking on a
   * row will open the infowindow for that marker.
   *
   * @param {Object} settings Hash of the settings for the store locator setup
   * @param {string} settings.sizingElement A selector of the element to size
   * the store locator against. Usually this is the map. It will make sure
   * that it fits height wise within this element and when the page is resized
   * it will resize this.
   * @param {string} settings.containerElement The element to insert the store
   * locator into.
   * @param {Object} settings.locationData This is the location data to be
   * able to render the whole store locator. This is usually per.mapRS.
   *
   * @return {BatchGeoStoreLocator}
   *
   * @constructor
   */
  var BatchGeoStoreLocator = function (settings) {
    var self = this;
    this.map = settings.map;
    this.settings = settings;
    this.initialZoomHandled = false;

    // The store locator needs to track the original index of the rows to
    // match markers to the rows. However, we need to be careful not to
    // overwrite any original data passed in so store this information in
    // double underscore private properties.
    this.locationData = this.settings.locationData.map(
      function (locationRow, locationRowIndex) {
        locationRow.__store_locator_id__ = locationRowIndex;
        return locationRow;
      },
    );

    this.search = new BatchGeoStoreLocatorSearch();
    this.noResultsView = new BatchGeoStoreLocatorNoResultsView();
    this.lazyLoad = new window.LazyLoad({
      elements_selector: ".lazy-storeLocator-image",
    });

    this._cacheElements();
    this.render();
    this.updateLocations();
    this._initEventListeners();

    // If we are on mobile initially hide the store locator
    BatchGeoStore.dispatch({
      type: "STORE_LOCATOR_VISIBILITY",
      visibility: !this._isWindowMobileWidth(),
    });

    if (!this._isWindowMobileWidth() && !BatchGeo.iframeDetect()) {
      BatchGeoStore.dispatch({
        type: "USER_LOCATION_ACTIVE_ENABLE",
      });
    }

    // When the storelocator visibility is toggled to a true
    // we want to reposition the list. This gaurantees that
    // the list position is correct regardless of info window
    // state.
    BatchGeoStore.addListener("StoreLocator", "visibility", function (data) {
      if (data.visibility) {
        self.repositionList();
      }
    });

    // When the first user location comes in we want to zoom to the closest
    // marker on the initial page load
    BatchGeoStore.addListener(
      "UserLocation",
      "latitude",
      function (userLocation) {
        if (!self.initialZoomHandled) {
          self.zoomToClosestResult(userLocation);
          self.initialZoomHandled = true;
        }
      },
    );

    return this;
  };

  /**
   * Will fit the first listItem (location in the store locator) within a
   * bounds with the location you give it. Usually this is the user's location
   * so you can give user some context of where they are in relation to the
   * closest location. We make an assumption within this method that the first
   * result is the closest since the sort order should be based on the lowest
   * distance from the user.
   *
   * This method also auto opens the infowindow for the closest list item.
   *
   * @param {{latitude: number, longitude:number}} location The latitude and
   * longitude of a location to zoom out to fit in a bounds that includes the
   * closest marker to this location.
   */
  BatchGeoStoreLocator.prototype.zoomToClosestResult = function (location) {
    if (location.latitude === null || this.listItems.length === 0) return;

    var listItem = this.listItems[0].getModel();
    var markersObjArray = window.batchGeoGoogleMapsMarker.getMarkersObjArray();
    var marker;

    if (markersObjArray[listItem.id] && markersObjArray[listItem.id].marker) {
      marker = markersObjArray[listItem.id].marker;
    } else {
      console.warn("No marker associated with list item");
      return;
    }

    // Create a bounds that contains the first item listed and the location
    // passed into the function.
    var bounds = new google.maps.LatLngBounds();
    bounds.extend(new google.maps.LatLng(listItem.lat, listItem.lon));
    bounds.extend(
      new google.maps.LatLng(location.latitude, location.longitude),
    );
    window.batchGeoGoogleMapsManager.getMap().fitBounds(bounds);

    // Pop open the nearest location's infowindow

    // Ensure we don't open infowindow with no markers on the map or with heatmap enabled.
    // on refresh /w heatmap enabled marker is null inside openPointWin (race condition? ¯\_(ツ)_/¯)
    // openPointWin checks the status of heatmap so we don't check before hand

    if (marker !== null) {
      window.batchGeoGoogleMapsMarker.MarkerInfoWindow._openPointWin(marker);
    }
  };

  /**
   * Will render the Store Locator and all it's subviews into the container
   * given in the settings. This will only render once. Future renders exit
   * early preventing dupe Store Locators.
   */
  BatchGeoStoreLocator.prototype.render = function () {
    var self = this;

    if (this.$elements.$wrapper) return;

    this.$elements.$wrapper = $("<div />", {
      addClass: "storeLocator",
      html: [this.$elements.$searchForm, this.$elements.$resultsWrapper],
      click: function (event) {
        self.listItemClickHandler(event);
      },
    }).appendTo(this.settings.containerElement);
  };

  /**
   * Caches, creates and attaches elements on the prototype under a $elements
   * namespace to provide easier and faster lookups.
   *
   * The elements returned are
   * - $sizingElement: This is the element that is used for keeping the store
   *   locator sized correctly. You could check changes it's size to trigger
   *   a resize handler or something
   * - $searchForm: The $wrapper element from the BatchGeoStoreLocatorSearch
   *   component
   * - $resultsWrapper: This element contains the location results which
   *   includes the result count header bar as well as the location list
   *   itself. It is a clusterized list too (meaning long lists wont have all
   *   of the DOM inside.)
   *
   * @example
   * storeLocatorInstance.$elements.$resultsWrapper
   *
   * @return {{$sizingElement: jQuery, $searchForm: jQuery, $resultsWrapper: jQuery}} this.$elements Returns an object with jQuery elements
   *
   * @private
   */
  BatchGeoStoreLocator.prototype._cacheElements = function () {
    return (this.$elements = {
      $sizingElement: $(this.settings.sizingElement),
      $searchForm: this.search.getElements().$wrapper,
      $resultsWrapper: $("<div />", {
        addClass: "storeLocator-results",
      }),
    });
  };

  /**
   * Repositions the list to be back in place based on the sizing element.
   */
  BatchGeoStoreLocator.prototype.repositionList = function (
    useCachedScrollPosition = false,
  ) {
    var offset = this.$elements.$sizingElement.offset();
    this.$elements.$wrapper.css({
      top: offset ? offset.top : "59px",
    });

    const scrollerTop = this.$elements.$scroller[0].getBoundingClientRect().top;
    const legWrapBarHeight = document.getElementById("legWrap").offsetHeight;
    const bodyBottom = document.body.getBoundingClientRect().bottom;
    const height = bodyBottom - scrollerTop - legWrapBarHeight;

    this.$elements.$scroller.css({
      // Take bottom of the body (let's say its 1000px tall) then subtract
      // where the scroller elements start (let's say top:250px). This
      // will result in a height of 750px which is the height we want the
      // scroll to be to fill the remaining space in the viewport.
      height,
    });

    if (useCachedScrollPosition && this.scrollPosition) {
      this.$elements.$scroller[0].scrollTop = this.scrollPosition;
      this.scrollPosition = 0;
    }
  };

  /**
   * Returns a boolean if you're at a mobile window width or not. It does not
   * check device type only sizing.
   *
   * @return {boolean}
   * @private
   */
  BatchGeoStoreLocator.prototype._isWindowMobileWidth = function () {
    return window.innerWidth <= 768;
  };

  /**
   * A method to put all the store listeners into. It makes sure they are only
   * ever set once so this method can be called as much as you want.
   *
   * @private
   */
  BatchGeoStoreLocator.prototype._initEventListeners = function () {
    var self = this;
    if (this._eventListenersSet) return;

    BatchGeoStore.addListener(
      "StoreLocator",
      "visibility",
      function (newState) {
        self.$elements.$wrapper.toggle(newState.visibility);
      },
    );

    BatchGeoStore.addListener(
      "MarkerLegend",
      function (newState, oldState) {
        // filteredMarkers could be the same but really we want to check if
        // a filter is applied OR if there is a change in the filtered
        // markers. If you don't check for both you may end up in a place
        // where page loads and you start with filteredMarkers: [] then they
        // search for something with 0 results which is also
        // filteredMarkers: [].
        return (
          newState.isFiltered !== oldState.isFiltered ||
          !_.isEqual(newState.filteredMarkers, oldState.filteredMarkers)
        );
      },
      function () {
        self.updateLocations();
      },
    );

    BatchGeoStore.addListener(
      "UserLocation",
      function (newState, oldState) {
        return (
          newState.latitude !== oldState.latitude ||
          newState.longitude !== oldState.longitude
        );
      },
      function () {
        self.updateLocations(true);
      },
    );

    BatchGeoStore.addListener(
      "StoreLocator",
      "searchLocation",
      function (newState, oldState) {
        if (newState.searchLocation != oldState.searchLocation) {
          self.updateLocations();
        }
      },
    );

    // As you resize BatchGeo we need to do some resizing, hiding, and
    // showing of some element. This watches for the resizing event.
    $(window).on("batchgeo.bgsize", function () {
      // If we had locator hidden, but resize back to desktop, show it
      var visible = BatchGeoStore.getState().StoreLocator.visibility;
      if (!visible && !self._isWindowMobileWidth()) {
        BatchGeoStore.dispatch({
          type: "STORE_LOCATOR_VISIBILITY",
          visibility: true,
        });
      }

      self.repositionList();
    });

    this._eventListenersSet = true;
  };

  /**
   * Delegated click event handler. It looks for the closest element with a
   * data-marker-id attribute. If it finds one it runs the item click handler
   * which focuses on the marker and opens up the info window.
   *
   * This is required here in the parent and not on the
   * BatchGeoStoreLocatorListItem's because they are put into clusterize which
   * strips the events during its appending process.
   *
   * @param {MouseEvent} event The event
   */
  BatchGeoStoreLocator.prototype.listItemClickHandler = function (event) {
    // Since we don't have a direct ref to the item object we need to get
    // the ID from the DOM.
    var markerId = $(event.target)
      .closest("[data-marker-id]")
      .data("marker-id");
    if (markerId !== undefined) {
      var marker =
        window.batchGeoGoogleMapsMarker.getMarkersAsObject()[markerId];
      if (!marker) {
        console.warn("No marker associated with clicked list item");
        return;
      }

      window.batchGeoGoogleMapsMarker.MarkerInfoWindow._openPointWin(marker);
      this.map.setCenter(marker.getPosition());
    }
  };

  /**
   * Creates elements and returns references to the scroller elements. This is
   * required because Clusterize requires an ID or an element. If we use ID
   * then we need to generate a UUID so there's no DOM ID collisions (which
   * will break CSS and selectors) but that doesn't seem as clean as simply
   * passing an element's reference.
   *
   * @return {{wrapper: HTMLDivElement, list: HTMLOListElement}}
   */
  BatchGeoStoreLocator.prototype.generateScrollerElements = function () {
    var self = this;
    var $scroller = $("<div />", {
      class: "storeLocator-scroller",
    });

    var $list = $("<ol />", {
      class: "storeLocator-list",
      html: self.listItems.map(function (listItem) {
        return listItem.render();
      }),
    }).appendTo($scroller);

    return {
      list: $list[0],
      scroller: $scroller[0],
    };
  };

  /**
   * This method creates and inserts the actual DOM. You can use this for
   * first time locator instances (although it is called in the constructor
   * for you) or later if you get new location data  ).
   * This will setup the clusterize stuff for smooth scrolling as well.
   */
  BatchGeoStoreLocator.prototype.updateLocations = function (
    cacheScrollPosition = false,
  ) {
    var locationData = this.getFilteredLocations() || [];
    var locationCount = locationData.length;
    var searchLocation = BatchGeoStore.getState().StoreLocator.searchLocation;

    if (
      cacheScrollPosition &&
      this.$elements.$scroller &&
      this.$elements.$scroller.length
    ) {
      // cache scroll position
      this.scrollPosition = this.$elements.$scroller[0].scrollTop;
    }

    // Generate locator list item instances for each location passed in.
    // This also merges in an `index` property (which is required) using the
    // Array#map index as it loops.
    const phoneIndex = this.settings.phoneIndex;
    this.listItems = locationData
      .map(function (locationRow, locationRowIndex) {
        return new BatchGeoStoreLocatorListItem(
          _.merge(locationRow, {
            id: locationRow.__store_locator_id__,
            index: locationRowIndex,
            addr:
              window.per.hideaddr_cb || !locationRow.addr
                ? ""
                : locationRow.addr,
            phone:
              phoneIndex > -1 && window.per.dataRS[locationRow.r]
                ? window.per.dataRS[locationRow.r][phoneIndex]
                : null,
          }),
        );
        // default sort = user's distance or if searching, distance from search
      })
      .sort(function (a, b) {
        if (searchLocation) {
          return (
            a.getDistanceFromSearchAddress(
              searchLocation.lat,
              searchLocation.lon,
              a.model.lat,
              a.model.lon,
            ) -
            b.getDistanceFromSearchAddress(
              searchLocation.lat,
              searchLocation.lon,
              b.model.lat,
              b.model.lon,
            )
          );
        } else {
          let distanceA = a.getNumericDistance();
          let distanceB = b.getNumericDistance();
          return distanceA - distanceB;
        }
      });

    // Create a new version of the scroller elements since we are getting
    // new location data
    var scrollerElements = this.generateScrollerElements();

    // Cache for later use when repositioning
    this.$elements.$scroller = $(scrollerElements.scroller);

    this.$elements.$resultsWrapper.html([
      // This is the "10 locations" type header above the locator list
      $("<h4 />", {
        class: "storeLocator-header",
        text: BatchGeo.pluralize(locationCount, "Location"),
      }),
      locationCount ? scrollerElements.scroller : this.noResultsView.render(),
    ]);

    this.clusterize = new Clusterize({
      scrollElem: scrollerElements.scroller,
      contentElem: scrollerElements.list,
    });

    this.lazyLoad.update();
    this.repositionList(cacheScrollPosition);
  };
  /**
   * A handler for managing when filters are changed. You can override this if
   * you need different behavior on filtering.
   *
   * @param {Object|Array} locationData A list of locations to filter on. It
   * expects to be an object where the index == the ID of the marker OR an
   * array where the index of the array will match the index in the map.
   *
   * @return {Array|any} It will return an array if it is filtering. It will,
   * however, return whatever you pass it if the legend isFiltered=false
   *
   * @private
   */
  BatchGeoStoreLocator.prototype.getFilteredLocations = function () {
    var markerLegendState = BatchGeoStore.getState().MarkerLegend;
    var filteredMarkers = markerLegendState.filteredMarkers;

    return !markerLegendState.isFiltered
      ? this.locationData
      : filteredMarkers.map(function (markerId) {
          return this.locationData[markerId];
        }, this);
  };
  window.BatchGeoStoreLocator = BatchGeoStoreLocator;
})(window);
